import { useId, useState, type KeyboardEvent, type ReactNode } from 'react'
import { glossary } from '@/lib/runtime-config'
import { cn } from '@/lib/utils'

export interface GlossaryTermProps {
  term: string
  children: ReactNode
  className?: string
}

/**
 * WU-08c: runtime 注入された ui.glossary（CH_UI_GLOSSARY）に登録された用語へ
 * 説明 tooltip を付ける。
 *
 * - glossary 未注入 / term が未登録 → children をそのまま返す（DOM 追加ゼロ。
 *   既定表示は一切変わらない）
 * - hover 前提にしない: button 要素でタップ/クリックにより開閉し、
 *   Tab → Enter/Space でも到達・開閉できる。開いた状態で Escape を押すと閉じる
 * - 吹き出しはダーク面（朝礼ボード）・通常面のどちらでも読めるよう固定色にする
 */
export function GlossaryTerm({ term, children, className }: GlossaryTermProps) {
  const [open, setOpen] = useState(false)
  const tooltipId = useId()
  const entries = glossary()
  const description = entries?.[term]

  if (description === undefined) {
    return <>{children}</>
  }

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'Escape' && open) {
      e.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <span className="relative inline">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        aria-label={`${term} の用語説明`}
        className={cn(
          'inline cursor-help border-0 bg-transparent p-0 text-inherit [font:inherit]',
          'underline decoration-dotted underline-offset-2',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          className,
        )}
      >
        {children}
      </button>
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          className={cn(
            'absolute left-0 top-full z-20 mt-1 w-max max-w-[14rem]',
            'whitespace-normal rounded-md border border-slate-700 bg-slate-900 px-2 py-1',
            'text-xs font-normal normal-case leading-snug text-slate-100 shadow-lg',
          )}
        >
          {description}
        </span>
      )}
    </span>
  )
}
